import React, { createContext, useContext, useState, useCallback } from 'react';
import { Step } from 'react-joyride';
import OnboardingTour from '../components/onboarding/OnboardingTour';

interface TourContextType {
  startTour: (steps: Step[], tourId: string) => void;
  restartTour: (steps: Step[], tourId: string) => void;
}

const TourContext = createContext<TourContextType | undefined>(undefined);

export const useTour = () => {
  const context = useContext(TourContext);
  if (!context) {
    throw new Error('useTour must be used within a TourProvider');
  }
  return context;
};

export const TourProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [steps, setSteps] = useState<Step[]>([]);
  const [run, setRun] = useState(false);
  const [tourId, setTourId] = useState<string | null>(null);

  // Filter steps for mobile compatibility
  const filterStepsForMobile = useCallback((tourSteps: Step[]) => {
    const isMobile = window.innerWidth < 1024;
    if (!isMobile) return tourSteps;

    // Filter out sidebar-specific steps on mobile and add mobile-specific navigation step
    const filteredSteps = tourSteps.filter(step => {
      const target = step.target as string;
      const sidebarTargets = [
        'aside nav ul',
        'a[href="/app/clients"]',
        'a[href="/app/team"]', 
        'a[href="/app/content-creation"]',
        'a[href="/app/media"]',
        'a[href="/app/posts"]',
        'a[href="/app/one-click-creator"]',
        'a[href="/app/schedule"]'
      ];
      return !sidebarTargets.includes(target);
    });

    // Add mobile navigation step after the main content step
    const mainStepIndex = filteredSteps.findIndex(step => step.target === 'main');
    if (mainStepIndex !== -1) {
      const mobileNavStep: Step = {
        target: 'button.lg\\:hidden',
        content: `
          <div style="padding: 16px;">
            <div style="font-size: 18px; font-weight: bold; margin-bottom: 12px; color: #1e293b;">
              📱 Mobile Navigation
            </div>
            <p style="font-size: 14px; color: #64748b; margin-bottom: 12px; line-height: 1.5;">
              On mobile, tap this menu button to access all your essential tools: Clients, Team, Media, Posts, and more.
            </p>
            <div style="background: #e0f2fe; padding: 12px; border-radius: 8px; border-left: 4px solid #0ea5e9;">
              <strong style="color: #0369a1;">📱 Mobile Tip:</strong> 
              <span style="color: #0c4a6e; font-size: 13px;">All desktop sidebar features are available in this mobile menu</span>
            </div>
          </div>
        `,
        placement: 'bottom',
        disableBeacon: true,
      };
      filteredSteps.splice(mainStepIndex + 1, 0, mobileNavStep);
    }

    return filteredSteps;
  }, []);

  const startTour = useCallback((tourSteps: Step[], id: string) => {
    const hasCompletedTour = localStorage.getItem(`hasCompleted_${id}`);
    if (!hasCompletedTour) {
      const filteredSteps = filterStepsForMobile(tourSteps);
      setSteps(filteredSteps);
      setTourId(id);
      setRun(true);
    }
  }, [filterStepsForMobile]);

  const restartTour = useCallback((tourSteps: Step[], id: string) => {
    const filteredSteps = filterStepsForMobile(tourSteps);
    setSteps(filteredSteps);
    setTourId(id);
    setRun(true);
  }, [filterStepsForMobile]);

  const handleComplete = () => {
    if (tourId) {
      localStorage.setItem(`hasCompleted_${tourId}`, 'true');
    }
    setRun(false);
    setSteps([]);
    setTourId(null);
  };

  return (
    <TourContext.Provider value={{ startTour, restartTour }}>
      {children}
      <OnboardingTour
        run={run}
        steps={steps}
        onComplete={handleComplete}
      />
    </TourContext.Provider>
  );
};
